/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Where a class sits in its life, what may still be written to it, and what the
 * schedule shows by default. One definition, shared by the data hook and the UI,
 * so the grid and the actions that operate on it can never disagree.
 */

import type { Class } from "@lemonadetraining/contracts";
import { toDate } from "./firestoreTime";

/** The fields the lifecycle is derived from. */
export type ClassLifecycleInput = Pick<Class, "status" | "endTime">;

/**
 * - `upcoming` — scheduled and still ahead: the only state a write may target.
 * - `attendance-pending` — past its end time, but the hourly attendance sweep has
 *   not stamped it yet, so who showed up is genuinely not established. An athlete
 *   may still be checked in during this window, so nothing may claim finality.
 * - `completed` — swept: attendance is final.
 * - `cancelled` — the class did not run.
 */
export type ClassLifecycle = "upcoming" | "attendance-pending" | "completed" | "cancelled";

/** What each state is called on screen. `upcoming` is the norm and stays unlabelled. */
export const CLASS_LIFECYCLE_LABEL: Record<ClassLifecycle, string | null> = {
  upcoming: null,
  "attendance-pending": "Attendance pending",
  completed: "Completed",
  cancelled: "Cancelled",
};

/** The state as a badge fits it: one word where the full label is two. */
export const shortClassStatusLabel = (label: string): string =>
  label === CLASS_LIFECYCLE_LABEL["attendance-pending"] ? "Pending" : label;

/**
 * Has the class's slot passed? Absent/unparseable end times read as "now" and
 * therefore as not-ended, which keeps an upcoming class's controls intact rather
 * than locking a class on malformed data.
 */
export function hasClassEnded(cls: ClassLifecycleInput, now: Date = new Date()): boolean {
  return toDate(cls.endTime).getTime() < now.getTime();
}

export function classLifecycle(cls: ClassLifecycleInput, now: Date = new Date()): ClassLifecycle {
  if (cls.status === "cancelled") return "cancelled";
  if (cls.status === "completed") return "completed";
  return hasClassEnded(cls, now) ? "attendance-pending" : "upcoming";
}

/** Is this class's end time present and parseable at all? */
function hasUsableEndTime(cls: ClassLifecycleInput): boolean {
  return Boolean(cls.endTime) && Number.isFinite(toDate(cls.endTime).getTime());
}

/**
 * May the schedule still write to this class (edit, delete, bulk-mutate)?
 *
 * Only a class that has not happened yet. Cancelling a class ends its bookings
 * and turns its checked-in bookings into cancellations, and nothing behind this
 * check re-imposes the rule — so the affordance itself must not exist once a
 * class is in the past.
 *
 * Unlike the display helpers, this one fails CLOSED: a class whose end time is
 * missing or unparseable cannot be shown to be still ahead, and an irreversible
 * delete is not the place to assume the benign reading.
 */
export function isClassEditable(cls: ClassLifecycleInput, now: Date = new Date()): boolean {
  return hasUsableEndTime(cls) && classLifecycle(cls, now) === "upcoming";
}

/**
 * Is this class's attendance FINAL — established by the sweep rather than still
 * being written?
 *
 * The counterpart to `isAttendanceEditable`, and deliberately not its negation:
 * a cancelled class is closed to writing but has no attendance to report, and a
 * class that merely ended has neither. Only a swept class has an answer, which
 * is what any surface reporting counts must wait for — reporting "0 no-show"
 * before the sweep is a confident wrong answer about real people, not a
 * missing one.
 */
export function isAttendanceFinal(cls: ClassLifecycleInput, now: Date = new Date()): boolean {
  return classLifecycle(cls, now) === "completed";
}

/**
 * May a roster still record attendance for this class — check an athlete in,
 * bulk-select, or otherwise change who was there?
 *
 * Until attendance is FINAL, which is not the same moment the class ends.
 * A class stays open through `attendance-pending`, the window between its end
 * time and the hourly sweep, because that window exists on purpose: `canCheckIn`
 * has no upper bound, the callable lets a coach or admin check someone in at any
 * time, and the sweep waits a full hour and carries a precondition specifically
 * so a late check-in is never overwritten. Closing at `endTime` would take away
 * the ordinary workflow of walking to the desk after a session and recording who
 * actually trained — and nothing else on any platform can do it.
 *
 * Once the sweep has stamped the class `completed`, attendance IS the record:
 * changing it is a correction, which is a separate, admin-only flow rather than
 * a button sitting on the roster. A `cancelled` class is closed too, however far
 * ahead its slot still is — nobody attends a class that is not running.
 *
 * Unlike `isClassEditable`, this one fails OPEN on a missing or unparseable end
 * time: `classLifecycle` reads an unusable end time as "not ended yet", so a
 * class with malformed data stays checkinable. That is the deliberate choice —
 * check-in is the coach's job at the door, and locking out the class happening
 * right now over bad data is the worse failure. The write it guards is also
 * correctable, unlike the delete `isClassEditable` guards, which is why that
 * one fails closed instead.
 */
export function isAttendanceEditable(cls: ClassLifecycleInput, now: Date = new Date()): boolean {
  const lifecycle = classLifecycle(cls, now);
  return lifecycle === "upcoming" || lifecycle === "attendance-pending";
}

/**
 * May this class be copied forward — duplicated into another week, or captured
 * into a schedule template?
 *
 * Anything that ran can: repeating last week is the point of copying it. A class
 * that was cancelled is not part of the week's shape, and the duplicateWeek
 * callable drops cancelled classes server-side, so counting them here would
 * overstate the write a preview is about to describe.
 */
export function isClassDuplicatable(cls: ClassLifecycleInput, now: Date = new Date()): boolean {
  return classLifecycle(cls, now) !== "cancelled";
}

/**
 * Does this class belong in the schedule's DEFAULT view (no status filter picked)?
 *
 * Everything the box lived through does. A cancelled class is the one exception:
 * it stays out of the week until its slot has passed, so the week ahead reads as
 * the plan and the week behind reads as the record.
 *
 * Note this keys on the SLOT, not on when the cancellation was made — a class
 * document's `cancelledAt` records when it was called off, not when it would have
 * run, so a class called off a week in advance is
 * indistinguishable from one called off on the morning, and both appear once
 * their slot is behind us. Picking a status in the filter bar overrides all of it.
 */
export function isVisibleByDefault(cls: ClassLifecycleInput, now: Date = new Date()): boolean {
  return cls.status !== "cancelled" || hasClassEnded(cls, now);
}
